import moment from 'moment'
import { useMemo, useState } from 'react'

import { Booking } from '../../models/Booking'
import { Lesson } from '../../models/Lesson'
import { Schedule } from '../../models/Schedule'
import { Icon } from '../Icon'
import Button from '../app/design/Button'
import Modal from '../app/design/Modal'
import Paragraph from '../app/design/Paragraph'
import { EmployeeSearchableSelect } from '../contacts/EmployeeSearchableSelect'
import { SelectDaysOfTheWeek } from '../common/SelectDaysOfTheWeek'
import { Checkbox } from '../common/fields/Checkbox'
import { DatePicker } from '../common/fields/DatePicker'
import { Select } from '../common/fields/Select'
import { TextField } from '../common/fields/TextField'
import { timeOfDayOptions } from '../settings/Options'
import { WorkflowIconSection } from '../workflows/WorkflowIconSection'

type EditType = 'single' | 'series' | 'new'

type Props = {
    isOpen: boolean
    onClose: () => void
    onDone: (lesson: Lesson, type: EditType, shouldNotify: boolean) => void
    lesson: Lesson
    allowRepeatForever?: boolean
    type?: EditType
    count?: number
}

export function EditLessonScheduleModal({
    isOpen,
    onClose,
    onDone,
    lesson,
    allowRepeatForever = false,
    type = 'new',
    count,
}: Props) {
    const [scratchLesson, setScratchLesson] = useState<Lesson>(lesson)

    const durationOptions = Booking.durationStrings(false, false)

    const instancesOptions = useMemo(() => {
        if (allowRepeatForever) {
            return Schedule.instanceStrings(Lesson.CLASS_INSTANCES)
        }
        return Schedule.instanceStrings(Lesson.CLASS_INSTANCES).slice(1)
    }, [allowRepeatForever])

    const selectedInterval = useMemo(() => {
        const val = `${scratchLesson.schedule?.interval}${scratchLesson.schedule?.frequency === 'monthly' ? 'm' : ''}`
        return instancesOptions.find((o) => o.id === val)?.label ?? 'Choose interval'
    }, [scratchLesson, instancesOptions])

    const [isRecurring, setIsRecurring] = useState(scratchLesson.isRecurring)

    const canSubmit = useMemo(() => {
        if (type !== 'new' || !isRecurring) {
            return Boolean(scratchLesson.duration && scratchLesson.date && scratchLesson.capacity)
        }

        return Boolean(
            scratchLesson.duration &&
                scratchLesson.date &&
                scratchLesson.capacity &&
                scratchLesson.schedule?.dow &&
                scratchLesson.schedule?.interval &&
                scratchLesson.schedule?.frequency &&
                (allowRepeatForever || scratchLesson.schedule?.instances)
        )
    }, [scratchLesson, isRecurring, allowRepeatForever])

    const selectedTimeOption = timeOfDayOptions.find(
        (o) => o.label === scratchLesson.date?.format('h:mma')
    )

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="large"
            title={
                type === 'new' ? 'Add lesson' : type === 'series' ? 'Edit series' : 'Edit lesson'
            }
            footer={
                <div className="flex items-center justify-end gap-x-4">
                    <Button
                        intent="tertiary_deprecated"
                        disabled={!canSubmit}
                        onClick={() => {
                            if (canSubmit) {
                                // just before submitting, set the date on the schedule if it's a recurring lesson
                                if (isRecurring && scratchLesson.schedule) {
                                    scratchLesson.schedule.date = scratchLesson.date
                                    scratchLesson.schedule.duration = scratchLesson.duration
                                }
                                onDone(scratchLesson, type, false)
                                onClose()
                            }
                        }}
                    >
                        Save
                    </Button>

                    <Button
                        intent="primary"
                        disabled={!canSubmit}
                        onClick={() => {
                            if (canSubmit) {
                                // just before submitting, set the date on the schedule if it's a recurring lesson
                                if (isRecurring && scratchLesson.schedule) {
                                    scratchLesson.schedule.date = scratchLesson.date
                                    scratchLesson.schedule.duration = scratchLesson.duration
                                }
                                onDone(scratchLesson, type, true)
                                onClose()
                            }
                        }}
                    >
                        Save & notify
                    </Button>
                </div>
            }
        >
            <div className="flex flex-col gap-12">
                {type === 'series' && count && (
                    <div className="border-primary-700 -mt-4 rounded-lg border p-4 text-center">
                        <Paragraph>
                            You are editing a series of{' '}
                            <strong>
                                {count} {count === 1 ? 'lesson' : 'lessons'}.
                            </strong>
                        </Paragraph>
                        <Paragraph>
                            If you edit the schedule, all lessons in the series will be updated.
                        </Paragraph>
                    </div>
                )}
                <WorkflowIconSection
                    title="Duration (required)"
                    icon={<Icon icon="clock.large" background="round" />}
                >
                    <Select
                        size="medium"
                        align="end"
                        options={durationOptions.map((option) => ({
                            id: option.id ?? '',
                            title: option.label ?? '',
                        }))}
                        selectedOptionID={scratchLesson.duration?.toString()}
                        onSelectOption={(option) => {
                            setScratchLesson(scratchLesson.clone({ duration: option.id }))
                        }}
                    />
                </WorkflowIconSection>

                <WorkflowIconSection
                    title="Start date (required)"
                    icon={<Icon icon="calendar2.large" background="round" />}
                >
                    <DatePicker
                        size="medium"
                        selectedDateString={
                            scratchLesson.date
                                ? moment(scratchLesson.date).format('MM/DD/YYYY')
                                : undefined
                        }
                        placeholder="Select date"
                        onSelectDate={(date) => {
                            setScratchLesson(
                                scratchLesson.clone({
                                    date: moment(date).add(
                                        Number(selectedTimeOption?.value ?? '0') * 60,
                                        'minutes'
                                    ),
                                })
                            )
                        }}
                    />
                </WorkflowIconSection>

                <WorkflowIconSection
                    title="Start time (required)"
                    icon={<Icon icon="alarmClock.large" background="round" />}
                    disabled={!scratchLesson.date}
                >
                    <Select
                        size="medium"
                        isDisabled={!scratchLesson.date}
                        className="min-w-36"
                        selectedOptionID={selectedTimeOption?.value}
                        options={timeOfDayOptions.map((option) => ({
                            id: option.value ?? '',
                            title: option.label ?? '',
                        }))}
                        onSelectOption={(option) => {
                            const newDate = moment(scratchLesson.date)
                                .startOf('day')
                                .add(Number(option.id) * 60, 'minutes')
                            setScratchLesson(scratchLesson.clone({ date: newDate }))
                        }}
                    />
                </WorkflowIconSection>

                <WorkflowIconSection
                    title="Class size (required)"
                    icon={<Icon icon="hash.large" background="round" />}
                >
                    <TextField
                        size="medium"
                        type="number"
                        value={scratchLesson.capacity?.toString()}
                        placeholder="Enter a class size"
                        min={0}
                        onChange={(e) => {
                            setScratchLesson(
                                scratchLesson.clone({ capacity: parseInt(e.target.value) })
                            )
                        }}
                    />
                </WorkflowIconSection>

                <WorkflowIconSection
                    title="Instructor"
                    icon={<Icon icon="employeeCard.large" background="round" />}
                >
                    <EmployeeSearchableSelect
                        selectedEmployee={scratchLesson.employee}
                        onSelectEmployee={(employee) => {
                            setScratchLesson((l) => l.clone({ employee }))
                        }}
                    />
                </WorkflowIconSection>

                {type === 'new' && (
                    <WorkflowIconSection
                        title="Recurring lessons"
                        icon={<Icon icon="repeat.large" background="round" />}
                        description="Select whether this lesson repeats"
                        hasHoverEffect
                        secondaryView={
                            <Checkbox isChecked={isRecurring} onChange={setIsRecurring} />
                        }
                    />
                )}

                {type === 'new' && isRecurring && (
                    <div className="-mt-3 flex flex-col gap-y-12">
                        <WorkflowIconSection
                            title="Repeat every (required)"
                            icon={<span className="size-12" />}
                        >
                            <Select
                                size="medium"
                                selectedOptionID={selectedInterval}
                                options={Schedule.intervalStrings().map((option) => ({
                                    id: option.id ?? '',
                                    title: option.label ?? '',
                                }))}
                                onSelectOption={(option) => {
                                    setScratchLesson(
                                        scratchLesson.clone({
                                            schedule: (
                                                scratchLesson.schedule ?? new Schedule()
                                            ).clone({
                                                interval: parseInt(option.id),
                                                frequency: option.id.endsWith('m')
                                                    ? 'monthly'
                                                    : 'weekly',
                                            }),
                                        })
                                    )
                                }}
                            />
                        </WorkflowIconSection>

                        <WorkflowIconSection
                            title="Days of the week (required)"
                            icon={<span className="size-12" />}
                        >
                            <SelectDaysOfTheWeek
                                schedule={scratchLesson.schedule?.dow}
                                onChange={(dow) => {
                                    const newSchedule = scratchLesson.schedule ?? new Schedule()
                                    newSchedule.dow = dow

                                    setScratchLesson(scratchLesson.clone({ schedule: newSchedule }))
                                }}
                            />
                        </WorkflowIconSection>
                        <WorkflowIconSection
                            title="Number of lessons (required)"
                            icon={<span className="size-12" />}
                        >
                            <Select
                                size="medium"
                                align="end"
                                selectedOptionID={
                                    scratchLesson.schedule?.instances?.toString() ?? ''
                                }
                                options={instancesOptions.map((option) => ({
                                    id: option.id ?? '',
                                    title: option.label ?? '',
                                }))}
                                onSelectOption={(option) => {
                                    const newSchedule = scratchLesson.schedule ?? new Schedule()
                                    setScratchLesson(
                                        scratchLesson.clone({
                                            schedule: newSchedule.clone({
                                                instances:
                                                    option.id === '' ? '' : parseInt(option.id),
                                            }),
                                        })
                                    )
                                }}
                            />
                        </WorkflowIconSection>
                    </div>
                )}
            </div>
        </Modal>
    )
}
